import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % heroSlides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover object-top"
            loading={i === 0 ? 'eager' : 'lazy'}
            title={`${s.title} - 吃瓜网站`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-semibold mb-5">
              <i className="ri-fire-line text-sm" />
              {slide.category}
              <span className="bg-background-50/20 px-1.5 py-0.5 rounded text-[10px]">{slide.tag}</span>
            </span>

            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4 animate-[fadeInUp_0.6s_ease-out]">
              {slide.title}
            </h2>

            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-xl leading-relaxed">
              {slide.excerpt}
            </p>

            <div className="flex items-center gap-4">
              <Link
                to={`/article/${slide.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all cursor-pointer"
              >
                查看详情
                <i className="ri-arrow-right-line" />
              </Link>
              <span className="text-sm text-background-50/60">{slide.date}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current
                ? 'bg-primary-500 w-8'
                : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到第${i + 1}条`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroSlides.length) % heroSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 transition-all cursor-pointer backdrop-blur-sm"
        aria-label="上一条"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/40 transition-all cursor-pointer backdrop-blur-sm"
        aria-label="下一条"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </section>
  );
}